import type { EquipmentItem } from '../api/station/stationDefaultsApiTypes';

/**
 * The editors' side of D15 (product refactor 4.4): the per-booking limit and
 * the equipment checklist, a product's default and a station's override.
 * Which value applies is the server's (`effectiveMaxPerBooking` /
 * `effectiveEquipment` in `@kolula/shared`, sent as `effective`); this file
 * only turns form input into the values the API takes, and back.
 */

/**
 * The use cases whose bookings show the equipment check (#1604): the
 * end-user app runs the equipment-rental flow, which holds the check screen,
 * for exactly these (`apps/end-user/src/modules/useOrder/UseOrderPage.tsx`).
 * A checklist on any other product is read by nothing, so it is not offered.
 */
export const EQUIPMENT_CHECK_USE_CASES = ['sup', 'rental'];

export const hasEquipmentCheck = (useCase: string | null | undefined): boolean =>
    !!useCase && EQUIPMENT_CHECK_USE_CASES.includes(useCase.toLowerCase());

/** A limit as typed: '' = none (no cap / inherit); a whole number >= 1; anything else invalid. */
export const parseLimitInput = (text: string): { ok: true, value: number | null } | { ok: false } => {
    const trimmed = text.trim();
    if (trimmed === '') return { ok: true, value: null };
    if (!/^\d+$/.test(trimmed)) return { ok: false };
    const value = Number(trimmed);
    return Number.isSafeInteger(value) && value >= 1 ? { ok: true, value } : { ok: false };
};

export const limitText = (value: number | null | undefined): string => (value == null ? '' : String(value));

/** One checklist row while it is edited. `key` is null for a row added in this edit. */
export interface ChecklistRow {
    /** Stable per row for React; the item's key once it has one. */
    id: string;
    key: string | null;
    labelDe: string;
    labelEn: string;
    noteDe: string;
    noteEn: string;
    /** The stored item, so what the editor does not show (other languages, `required`) is kept. */
    original: EquipmentItem | null;
}

let rowCounter = 0;
const nextRowId = () => {
    rowCounter += 1;
    return `new-${rowCounter}`;
};

export const toChecklistRows = (items: EquipmentItem[] | null | undefined): ChecklistRow[] =>
    (items ?? []).map((item) => ({
        id: item.key,
        key: item.key,
        labelDe: item.label_i18n?.de ?? '',
        labelEn: item.label_i18n?.en ?? '',
        noteDe: item.safety_note_i18n?.de ?? '',
        noteEn: item.safety_note_i18n?.en ?? '',
        original: item,
    }));

export const emptyChecklistRow = (): ChecklistRow => ({
    id: nextRowId(), key: null, labelDe: '', labelEn: '', noteDe: '', noteEn: '', original: null,
});

const FOLD: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss', å: 'aa', æ: 'ae', ø: 'oe' };

/**
 * A key for a new item, from its English label (else the German one): the
 * check screen and the issue report find an item by its key, so it must be
 * unique in the list and never change once stored — existing items keep theirs.
 */
export const newEquipmentKey = (label: string, taken: Set<string>): string => {
    const base = label
        .toLowerCase()
        .replace(/[äöüßåæø]/g, (char) => FOLD[char] ?? char)
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .split('_')
        .filter(Boolean)
        .join('_')
        .slice(0, 48) || 'item';
    let key = base;
    let n = 2;
    while (taken.has(key)) {
        key = `${base}_${n}`;
        n += 1;
    }
    return key;
};

/** Every row has both labels: the API requires DE and EN. */
export const checklistComplete = (rows: ChecklistRow[]): boolean =>
    rows.every((row) => row.labelDe.trim() !== '' && row.labelEn.trim() !== '');

const withTexts = (base: Record<string, string> | undefined, de: string, en: string) => {
    const out: Record<string, string> = { ...(base ?? {}) };
    delete out.de;
    delete out.en;
    if (de.trim()) out.de = de.trim();
    if (en.trim()) out.en = en.trim();
    return out;
};

/**
 * The rows as the stored list. New rows get a key; stored rows keep theirs.
 *
 * `reserved`: every key the lists this edit started from carried. A key
 * removed in the same edit is never handed to a new item: issue reports match
 * flagged items by key, so a reused key would relabel an old report.
 */
export const fromChecklistRows = (rows: ChecklistRow[], reserved: readonly string[] = []): EquipmentItem[] => {
    const taken = new Set([...reserved, ...rows.map((row) => row.key).filter((key): key is string => !!key)]);
    return rows.map((row) => {
        const key = row.key ?? newEquipmentKey(row.labelEn.trim() || row.labelDe.trim(), taken);
        taken.add(key);
        const item: EquipmentItem = { key, label_i18n: withTexts(row.original?.label_i18n, row.labelDe, row.labelEn) };
        if (row.original?.required !== undefined) item.required = row.original.required;
        const note = withTexts(row.original?.safety_note_i18n, row.noteDe, row.noteEn);
        if (Object.keys(note).length > 0) item.safety_note_i18n = note;
        return item;
    });
};

/** The keys of stored lists, for `fromChecklistRows`'s `reserved`. */
export const checklistKeys = (...lists: (EquipmentItem[] | null | undefined)[]): string[] =>
    lists.flatMap((list) => (list ?? []).map((item) => item.key));

/** The item labels in the admin's language, for the view. */
export const checklistLabels = (items: EquipmentItem[] | null | undefined, language: string): string[] => {
    const lang = language.toLowerCase().startsWith('de') ? 'de' : 'en';
    return (items ?? []).map((item) => item.label_i18n?.[lang] ?? item.label_i18n?.de ?? item.key);
};
